import { createTraditionalReservation } from '@features/booking/api/reservations';
import { createWlTraditionalReservation } from '@features/booking/api/waitingList';
import type { TraditionalGrabPayload } from '@features/booking/api/reservations';
import type { WlTraditionalPayload } from '@features/booking/api/waitingList';
import type { BaseReservationFields, CommitContext, CommitDerived } from './shared';
import { persistSuccessAndAdvance } from './shared';

/**
 * Traditional reservation path — no payment, no 3DS.
 *
 * When bypassesGuarantee is true (gift card / reschedule of guaranteed
 * reservation), feeDeposit is forced to 0 even if booking still holds a
 * stale value from an earlier step.
 *
 * WL variant (createWlTraditionalReservation) is used when flowType is
 * 'waiting_list'; otherwise the standard endpoint.
 */
export async function commitTraditional(
  ctx: CommitContext,
  derived: CommitDerived,
  basePayload: BaseReservationFields,
): Promise<void> {
  const { booking } = ctx;
  const { isWaitingList, bypassesGuarantee } = derived;

  // When bypassesGuarantee, the stored feeDeposit may still hold a stale value
  // from a slot/menu that originally required a guarantee — send 0 to keep the
  // persisted reservation consistent.
  const traditionalFee = bypassesGuarantee ? 0 : booking.feeDeposit;

  if (isWaitingList) {
    const wlPayload: WlTraditionalPayload = {
      ...basePayload,
      date: booking.wlDates,
      menu: basePayload.menu as number, // WL menu pre-collapsed by buildBasePayload behavior
      type: 'traditional',
      fee: traditionalFee,
    };
    const response = await createWlTraditionalReservation(ctx.currentLanguage, wlPayload);
    persistSuccessAndAdvance(ctx, derived, response.reservationId, response.uniqueId, 'traditional');
  } else {
    const traditionalPayload: TraditionalGrabPayload = {
      ...basePayload,
      date: basePayload.date as string,
      menu: basePayload.menu as number,
      type: 'traditional',
      fee: traditionalFee,
    };
    const response = await createTraditionalReservation(ctx.currentLanguage, traditionalPayload);
    persistSuccessAndAdvance(ctx, derived, response.reservationId, response.uniqueId, 'traditional');
  }
}
